import { CalendarDays, MapPin } from "lucide-react";
import LunarNewYearHero from "@/components/LunarNewYearHero";
import HomePromoHero from "@/components/HomePromoHero";
import FeaturedEventPrice from "@/components/FeaturedEventPrice";
import { useHomepagePromotionPricing } from "@/hooks/useHomepagePromotionPricing";
import { EventAction } from "@/components/FeaturedEventAction";
import { eventFeaturedArtwork } from "@shared/eventPlaceholderArt";
import { EVENTS, type CHIEvent } from "@/lib/eventsData";
import { trpc } from "@/lib/trpc";
import { isPastCalendarEvent } from "@/lib/calendarFilters";
import { useEventClock } from "@/hooks/useEventClock";

const LNY_SLUG = "lunar-new-year-celebration-2027";
const LNY_EVENT_SLUG = "lunar-new-year-event-2027";
const REFRESH_MS = 30_000;

function DynamicEventHero({ event }: { event: CHIEvent & { generatedImage?: string | null } }) {
  const pricing = useHomepagePromotionPricing(event.slug);
  const artwork = eventFeaturedArtwork(event, { width: 1800, height: 1000 });
  const dateLabel = event.displayDate || event.date;
  return (
    <section className="featured-event" aria-label={`Featured event: ${event.title}`} style={{ backgroundImage: `linear-gradient(90deg,rgba(7,11,24,.94),rgba(7,11,24,.78) 46%,rgba(7,11,24,.25)),url("${artwork.url}")` }}>
      <div className="featured-event__inner">
        <p className="featured-event__eyebrow">Featured event</p>
        <h1>{event.title}</h1>
        {event.subtitle && <p className="featured-event__subtitle">{event.subtitle}</p>}
        <div className="featured-event__facts">
          <span><CalendarDays size={17} />{dateLabel}{event.time ? ` · ${event.time}` : ""}</span>
          {event.location && <span><MapPin size={17} />{event.location}</span>}
        </div>
        <FeaturedEventPrice pricing={pricing} />
        <div className="featured-event__cta"><EventAction event={event} pricing={pricing} /></div>
      </div>
      <style>{`
        .featured-event{position:relative;display:flex;align-items:center;min-height:min(760px,calc(100svh - var(--chi-partners-band,0px)));padding:clamp(5rem,10vw,8rem) max(1.25rem,calc((100vw - 1200px)/2));background-position:center;background-size:cover;color:#fff;overflow:hidden}
        .featured-event__inner{width:min(100%,680px);padding:clamp(1rem,3vw,2rem) 0}
        .featured-event__eyebrow{margin:0 0 1rem;color:#F4C7DE;text-transform:uppercase;letter-spacing:.19em;font:700 .75rem 'Space Grotesk',sans-serif}
        .featured-event h1{max-width:12ch;margin:0;color:#fff;font:700 clamp(2.7rem,7vw,6.4rem)/.98 'Bebas Neue',sans-serif;letter-spacing:.025em}
        .featured-event__subtitle{max-width:34rem;margin:.8rem 0 0;color:#E8E7EE;font:400 clamp(1rem,2vw,1.3rem)/1.5 'Space Grotesk',sans-serif}
        .featured-event__facts{display:flex;flex-wrap:wrap;gap:.8rem 1.35rem;margin:1.7rem 0;color:#F0EAF2;font:500 .95rem/1.5 'Space Grotesk',sans-serif}
        .featured-event__facts span{display:inline-flex;align-items:flex-start;gap:.5rem;max-width:100%}.featured-event__facts svg{flex:none;color:#F0A7CE}
        .featured-event__action{display:inline-flex;min-height:50px;align-items:center;justify-content:center;gap:.55rem;padding:.8rem 1.35rem;border-radius:999px;background:#E91E8C;color:white;text-decoration:none;font:700 .9rem 'Space Grotesk',sans-serif}
        .featured-event__action:focus-visible{outline:3px solid #fff;outline-offset:3px}
        @media(max-width:760px){.featured-event{min-height:620px;align-items:flex-end;padding:5rem 1.2rem 4rem;background-position:58% center}.featured-event__inner{padding:0}.featured-event h1{font-size:clamp(2.8rem,13vw,4.7rem)}.featured-event__facts{display:grid;gap:.65rem}.featured-event__action{width:100%}}
        @media(prefers-reduced-motion:reduce){.featured-event *{scroll-behavior:auto!important;animation:none!important;transition:none!important}}
      `}</style>
    </section>
  );
}

export default function HomepagePromotion() {
  const eventClock = useEventClock(30_000);
  const settingsQuery = trpc.homepagePromotion.settings.useQuery(undefined, {
    retry: false,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
  });
  const catalog = trpc.events.publicCatalog.useQuery(undefined, { retry: false, refetchOnWindowFocus: true });
  const settings = settingsQuery.data;
  if (settingsQuery.isLoading || catalog.isLoading) {
    return <div aria-label="Loading featured event" className="homepage-promo-skeleton" style={{ minHeight: "min(720px,100svh)", background: "linear-gradient(110deg,#111729,#20253a,#111729)", backgroundSize: "200% 100%", animation: "home-promo-shimmer 1.6s ease-in-out infinite" }}><style>{`@keyframes home-promo-shimmer{to{background-position:-200% 0}}@media(prefers-reduced-motion:reduce){.homepage-promo-skeleton{animation:none!important}}`}</style></div>;
  }
  if (settingsQuery.isError || settings?.mode === "off" || !settings?.eventSlug) return <HomePromoHero />;
  const managed = (catalog.data ?? []) as (CHIEvent & { generatedImage?: string | null; isArchived?: boolean; status?: string })[];
  const managedSlugs = new Set(managed.map(item => item.slug));
  const mergedCatalog = [
    ...EVENTS.filter(item => !managedSlugs.has(item.slug)),
    ...managed,
  ];
  const event = mergedCatalog.find(item => item.slug === settings.eventSlug) as (CHIEvent & { generatedImage?: string | null; isArchived?: boolean; status?: string }) | undefined;
  const eventAvailable = (settings as typeof settings & { eventAvailable?: boolean }).eventAvailable;
  // Preserve the pre-existing visual campaign even before its event is linked.
  // Explicit Off, archiving, missing unrelated events and purchase rules still win.
  if (settings.mode === "hero" && settings.eventSlug === LNY_SLUG && !event
    && !settings.eventArchived && !catalog.isError) return <LunarNewYearHero />;
  if (eventAvailable === false || !event || event.isArchived || event.status === "archived" || isPastCalendarEvent(event, eventClock)) return <HomePromoHero />;
  if (settings.mode === "button") return <HomePromoHero featuredEvent={event} />;
  if (event.slug === LNY_SLUG || event.slug === LNY_EVENT_SLUG) return <LunarNewYearHero event={event} />;
  return <DynamicEventHero event={event} />;
}
